import React, { useRef, useEffect, useState, useCallback, useMemo } from "react";

export default function CodeEditor({
  code,
  setCode,
  currentLine,
  setCurrentLine,
  activeRunButton,
  setActiveRunButton,
  assembleCode,
  executeStep,
  handleReset,
  runProgram,
  togglePause,
}) {
  const textareaRef = useRef(null);
  const gutterRef = useRef(null);
  const execHighlightRef = useRef(null); // execution line highlight
  const syntaxRef = useRef(null); // syntax layer
  const fileInputRef = useRef(null);

  const isAssembledRef = useRef(false);
  const isPausedRef = useRef(false);

  const [cursorLine, setCursorLine] = useState(0);

  const toggleComment = () => {
  const ta = textareaRef.current;
  if (!ta) return;

  const value = ta.value;
  const start = ta.selectionStart;
  const end = ta.selectionEnd;

  // Find full line range
  const lineStart = value.lastIndexOf("\n", start - 1) + 1;
  const lineEnd = value.indexOf("\n", end);
  const safeEnd = lineEnd === -1 ? value.length : lineEnd;

  const block = value.slice(lineStart, safeEnd);
  const lines = block.split("\n");

  const allCommented = lines.every(
    (l) => l.trim() === "" || l.trim().startsWith("#")
  );

  const newLines = lines.map((line) => {
    if (line.trim() === "") return line;

    return allCommented
      ? line.replace(/^(\s*)#\s?/, "$1")
      : line.replace(/^(\s*)/, "$1# ");
  });

  const newBlock = newLines.join("\n");
  const newValue =
    value.slice(0, lineStart) + newBlock + value.slice(safeEnd);

  // remove fake first line before storing
  setCode(newValue.startsWith("\n") ? newValue.slice(1) : newValue);

  // restore selection
  requestAnimationFrame(() => {
    ta.selectionStart = start;
    ta.selectionEnd = end;
  });
};



  const latestLineRef = useRef(currentLine);
  useEffect(() => {
    latestLineRef.current = currentLine;
  }, [currentLine]);

  const [isDragging, setIsDragging] = useState(false);
  const dragDepthRef = useRef(0);

  // ==== Editor geometry (ONE source of truth) ====
  const LINE_H = 20;
  const GUTTER_W = 50;

  // ✅ IMPORTANT:
  // We do NOT use container paddingTop to align layers (causes caret mismatch).
  // Instead we add a FAKE FIRST LINE in both textarea + syntax layer.
  // const FAKE_TOP_LINE = "\n";

  // ---- Derived display text (what user sees / highlights) ----
  // const viewText = useMemo(() => FAKE_TOP_LINE + (code || ""), [code]);
  const viewText = code || "";
  const lines = useMemo(() => (code || "").split("\n"), [code]);

  
const highlightCode = (src) => {
  // ---- pass 1: collect label names ----
  const labelSet = new Set();
  src.split("\n").forEach((line) => {
    const m = line.match(/^([A-Za-z_]\w*):/);
    if (m) labelSet.add(m[1]);
  });

  return src
    .split("\n")
    .map((line) => {
      // split comment from code
      const hashIndex = line.indexOf("#");
      let codePart = line;
      let commentPart = "";

      if (hashIndex !== -1) {
        codePart = line.slice(0, hashIndex);
        commentPart = line.slice(hashIndex);
      }

      // ---- escape HTML first ----
      let highlighted = codePart
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;");

      // ---- label DEFINITIONS ----
      highlighted = highlighted.replace(
        /^([A-Za-z_]\w*):/,
        `<span style="color:#C586C0">$1</span>:`
      );

      // ---- instructions ----
      highlighted = highlighted.replace(
        /\b(add|sub|mul|div|and|or|xor|sll|srl|addi|andi|ori|xori|lw|sw|beq|bne|blt|bge|jal|jalr|lui)\b/gi,
        `<span style="color:#569CD6">$1</span>`
      );

      // ---- registers ----
      highlighted = highlighted.replace(
        /\b(x\d+)\b/g,
        `<span style="color:#9CDCFE">$1</span>`
      );

      // ---- immediates ----
      highlighted = highlighted.replace(
        /\b(-?\d+|0x[0-9a-fA-F]+)\b/g,
        `<span style="color:#D7BA7D">$1</span>`
      );

      // ---- label REFERENCES (HTML-safe) ----
      highlighted = highlighted.replace(
        /\b([A-Za-z_]\w*)\b/g,
        (m, word, offset, full) => {
          // don't touch inside <span ...>
          const before = full.slice(0, offset);
          const open = before.lastIndexOf("<");
          const close = before.lastIndexOf(">");
          if (open > close) return m;

          return labelSet.has(word)
            ? `<span style="color:#C586C0">${word}</span>`
            : word;
        }
      );

      // ---- append COMMENT part ----
      if (commentPart) {
        highlighted += `<span style="color:#6A9955">${commentPart
          .replace(/&/g, "&amp;")
          .replace(/</g, "&lt;")
          .replace(/>/g, "&gt;")}</span>`;
      }

      return highlighted;
    })
    .join("\n");
};

  const highlightedHTML = useMemo(() => highlightCode(viewText), [viewText]);

  /* ================= EDITOR MODE ================= */
  const editorMode =
    activeRunButton === "run"
      ? "running"
      : activeRunButton === "pause" || activeRunButton === "step"
      ? "paused"
      : isAssembledRef.current
      ? "assembled"
      : "edit";

  /* ================= RESET ================= */
  const resetToTop = useCallback(() => {
    setCurrentLine(0);
    setCursorLine(0);
    isPausedRef.current = false;

    const ta = textareaRef.current;
    if (ta) {
      ta.scrollTop = 0;
      ta.selectionStart = 0;
      ta.selectionEnd = 0;
      ta.focus();
    }

    if (gutterRef.current) {
      gutterRef.current.style.transform = `translateY(0px)`;
    }
    if (execHighlightRef.current) {
      execHighlightRef.current.style.transform = `translateY(0px)`;
    }
    if (syntaxRef.current) {
      syntaxRef.current.style.transform = `translateY(0px)`;
    }
  }, [setCurrentLine]);

  /* ================= FILE OPEN ================= */
  const handleFileOpen = useCallback(
    (e) => {
      const file = e.target.files?.[0];
      if (!file) return;

      const reader = new FileReader();
      reader.onload = () => {
        setCode(reader.result || "");
        isAssembledRef.current = false;
        setActiveRunButton("edit");
        resetToTop();
      };
      reader.readAsText(file);
      e.target.value = "";
    },
    [setCode, resetToTop, setActiveRunButton]
  );

  const doOpen = () => {
    setActiveRunButton("open");
    fileInputRef.current?.click();
  };

  /* ================= SAVE ================= */
  const doSave = async () => {
    setActiveRunButton("save");
    if (!window.showSaveFilePicker) return;

    try {
      const handle = await window.showSaveFilePicker({
        suggestedName: "program.rvx",
        types: [{ accept: { "text/plain": [".rvx", ".txt"] } }],
      });
      const writable = await handle.createWritable();
      await writable.write(code || "");
      await writable.close();
    } catch {}
  };

  /* ================= RUN CONTROLS ================= */
  const doAssemble = () => {
    setActiveRunButton("assemble");
    assembleCode();
    isAssembledRef.current = true;
    isPausedRef.current = false;
    resetToTop();
  };

  const doRun = () => {
  setActiveRunButton("run");
  isPausedRef.current = false;
  runProgram();

  // ❌ don't force focus during execution
};


//   const doPause = () => {
//   const ta = textareaRef.current;
//   const selStart = ta?.selectionStart;
//   const selEnd = ta?.selectionEnd;

//   togglePause();
//   isPausedRef.current = !isPausedRef.current;
//   setActiveRunButton(isPausedRef.current ? "pause" : "run");

//   requestAnimationFrame(() => {
//     if (ta && selStart != null && selEnd != null) {
//       ta.selectionStart = selStart;
//       ta.selectionEnd = selEnd;
//     }
//     ta?.focus();
//   });
// };

const doPause = () => {
  togglePause();
  isPausedRef.current = !isPausedRef.current;

  setActiveRunButton(isPausedRef.current ? "pause" : "run");

  // ❌ DO NOT touch focus
  // ❌ DO NOT touch selection
  // ❌ DO NOT scroll
};

  const doStep = () => {
  setActiveRunButton("step");
  executeStep();
  isPausedRef.current = true;

  // ❌ DO NOT focus textarea during step
};

  const doReset = () => {
    setActiveRunButton("edit");
    handleReset();
    isAssembledRef.current = false;
    isPausedRef.current = false;
    resetToTop();
  };

  /* ================= KEYBOARD SHORTCUTS (bulletproof) ================= */
  const actionsRef = useRef({
    doOpen: () => {},
    doSave: () => {},
    doAssemble: () => {},
    doRun: () => {},
    doPause: () => {},
    doStep: () => {},
    doReset: () => {},
  });

  useEffect(() => {
    actionsRef.current = { doOpen, doSave, doAssemble, doRun, doPause, doStep, doReset };
  }, [doOpen, doSave, doAssemble, doRun, doPause, doStep, doReset]);

  useEffect(() => {
    const handler = (e) => {
      const k = (e.key || "").toLowerCase();
      if (e.repeat) return;
      if (e.metaKey) return;

      const stopAll = () => {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation?.();
      };

      // Open: Shift+O OR Ctrl+O
      if ((e.shiftKey && !e.ctrlKey && !e.altKey && k === "o") || (e.ctrlKey && !e.altKey && k === "o")) {
        stopAll();
        actionsRef.current.doOpen();
        return;
      }

      // Save: Ctrl+S OR Ctrl+Shift+S
      if (e.ctrlKey && !e.altKey && k === "s") {
        stopAll();
        actionsRef.current.doSave();
        return;
      }

      // Assemble: Shift+A
      if (e.shiftKey && !e.ctrlKey && !e.altKey && k === "a") {
        stopAll();
        actionsRef.current.doAssemble();
        return;
      }

      // Run: Shift+R
      if (e.shiftKey && !e.ctrlKey && !e.altKey && k === "r") {
        stopAll();
        actionsRef.current.doRun();
        return;
      }

      // Pause: Shift+P
      if (e.shiftKey && !e.ctrlKey && !e.altKey && k === "p") {
        stopAll();
        actionsRef.current.doPause();
        return;
      }

      // Step: Shift+S (Ctrl+S handled above)
      if (e.shiftKey && !e.ctrlKey && !e.altKey && k === "s") {
        stopAll();
        actionsRef.current.doStep();
        return;
      }

      // Reset: Alt+R
      if (e.altKey && !e.ctrlKey && k === "r") {
        stopAll();
        actionsRef.current.doReset();
        return;
      }

 


    };

    document.addEventListener("keydown", handler, true);
    return () => document.removeEventListener("keydown", handler, true);
  }, []);

  /* ================= DRAG & DROP ================= */
  const onDragEnter = (e) => {
    e.preventDefault();
    dragDepthRef.current += 1;
    setIsDragging(true);
  };

  const onDragLeave = (e) => {
    e.preventDefault();
    dragDepthRef.current -= 1;
    if (dragDepthRef.current <= 0) {
      dragDepthRef.current = 0;
      setIsDragging(false);
    }
  };

  const onDragOver = (e) => {
    e.preventDefault();
    if (e.dataTransfer) e.dataTransfer.dropEffect = "copy";
  };

  const onDrop = (e) => {
    e.preventDefault();
    dragDepthRef.current = 0;
    setIsDragging(false);

    const file = e.dataTransfer.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => {
      setCode(reader.result || "");
      isAssembledRef.current = false;
      setActiveRunButton("edit");
      resetToTop();
    };
    reader.readAsText(file);
  };

  /* ================= CURSOR ================= */
  // const updateCursorLine = () => {
  //   const ta = textareaRef.current;
  //   if (!ta) return;

  //   // ✅ because textarea value has one fake line at the top, subtract 2
  //   const before = ta.value.slice(0, ta.selectionStart);
  //   // setCursorLine(Math.max(0, before.split("\n").length - 2));
  //   setCursorLine(before.split("\n").length - 1);


  //   ta.focus();
  // };

  const updateCursorLine = () => {
  // ✅ DO NOTHING unless user is editing
  if (editorMode !== "edit") return;

  const ta = textareaRef.current;
  if (!ta) return;

  const before = ta.value.slice(0, ta.selectionStart);
  setCursorLine(before.split("\n").length - 1);
};

  /* ================= SCROLL (sync all layers) ================= */
  const onScroll = () => {
    const ta = textareaRef.current;
    if (!ta) return;

    const y = -ta.scrollTop;
    const x = -ta.scrollLeft;

    // Gutter: only follow vertical
    if (gutterRef.current) {
      gutterRef.current.style.transform = `translateY(${y}px)`;
    }

    // Execution highlight: only vertical
    if (execHighlightRef.current) {
      execHighlightRef.current.style.transform = `translateY(${y}px)`;
    }

    // Syntax: follow both horizontal + vertical so it stays under text
    if (syntaxRef.current) {
      syntaxRef.current.style.transform = `translate(${x}px, ${y}px)`;
    }
  };

  // ✅ Keep gutter height synced to textarea scrollHeight (works with fake top line too)
  useEffect(() => {
    const ta = textareaRef.current;
    const gutter = gutterRef.current;
    if (!ta || !gutter) return;
    gutter.style.height = `${ta.scrollHeight}px`;
  }, [viewText]);

  // ✅ Auto-scroll to current execution line (account fake top line)
  // useEffect(() => {
  //   const ta = textareaRef.current;
  //   if (!ta) return;

  //   // +1 because visual content has a fake first line
  //   // const top = (currentLine + 1) * LINE_H;
  //   const top = currentLine * LINE_H;

  //   const bottom = top + LINE_H;

  //   if (top < ta.scrollTop) ta.scrollTop = top;
  //   else if (bottom > ta.scrollTop + ta.clientHeight) {
  //     ta.scrollTop = bottom - ta.clientHeight;
  //   }
  // }, [currentLine]);

  useEffect(() => {
  const ta = textareaRef.current;
  if (!ta) return;

  const viewportLines = Math.floor(ta.clientHeight / LINE_H);
  const targetScrollTop =
    (currentLine - Math.floor(viewportLines / 2)) * LINE_H;

  const clamped =
    Math.max(0, Math.min(targetScrollTop, ta.scrollHeight - ta.clientHeight));

  ta.scrollTop = clamped;
}, [currentLine]);


  /* ================= STYLES ================= */
  const btn = (name) => ({
    padding: "2px 8px",
    border: "1px solid #666",
    borderRadius: 4,
    background: activeRunButton === name ? "#0f0" : "#222",
    color: activeRunButton === name ? "#000" : "#fff",
    cursor: "pointer",
    fontFamily: "monospace",
    whiteSpace: "nowrap",
  });

  /* ================= LINKS ================= */
  const openExamples = () => window.open("https://github.com/YOUR_USERNAME/YOUR_REPO/tree/main/examples", "_blank");
  const openAbout = () => window.open("https://github.com/YOUR_USERNAME", "_blank");
  const openFeatures = () => window.open("https://github.com/YOUR_USERNAME/YOUR_REPO#features", "_blank");

  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <input ref={fileInputRef} type="file" accept=".rvx,.txt" style={{ display: "none" }} onChange={handleFileOpen} />

      {/* ================= HEADER ================= */}
      <div
        style={{
          border: "2px solid #333",
          borderRadius: 6,
          padding: 8,
          marginBottom: 8,
          background: "#111",
          display: "flex",
          flexDirection: "column",
          gap: 8,
        }}
      >
        {/* ROW 1 */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ fontFamily: "monospace", fontSize: 16, fontWeight: "bold" }}>RISC-V Code Editor</div>

            <div
              title={editorMode === "running" ? "Running" : editorMode === "paused" || editorMode === "assembled" ? "Paused / Assembled" : "Editing"}
              style={{
                width: 10,
                height: 10,
                borderRadius: "50%",
                background: editorMode === "running" ? "#00ff66" : editorMode === "paused" || editorMode === "assembled" ? "#ffcc00" : "#666",
                boxShadow: editorMode === "running" ? "0 0 6px #00ff66" : "none",
              }}
            />
          </div>

          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <button style={btn("examples")} onClick={openExamples}>
              Examples
            </button>
            <button style={btn("features")} onClick={openFeatures}>
              Features
            </button>
            <button style={btn("about")} onClick={openAbout}>
              About
            </button>
          </div>
        </div>

        {/* ROW 2 */}
        <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
          <button style={btn("open")} onClick={doOpen}>
            Open
          </button>
          <button style={btn("save")} onClick={doSave}>
            Save
          </button>
          <div style={{ flexGrow: 1 }} />
          <button style={btn("assemble")} onClick={doAssemble}>
            Assemble
          </button>
          <button style={btn("run")} onClick={doRun}>
            Run
          </button>
          <button style={btn("pause")} onClick={doPause}>
            ▶ / ⏸
          </button>
          <button style={btn("step")} onClick={doStep}>
            Step
          </button>
          <button style={btn("reset")} onClick={doReset}>
            Reset
          </button>
        </div>
      </div>

      {/* ================= EDITOR BODY ================= */}
      <div
        onDragEnter={onDragEnter}
        onDragLeave={onDragLeave}
        onDragOver={onDragOver}
        onDrop={onDrop}
        style={{
          flex: 1,
          display: "flex",
          border: "2px solid #333",
          borderRadius: 6,
          background: "#111",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* ✅ DRAG OVERLAY */}
        {isDragging && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              zIndex: 20,
              background: "rgba(0,0,0,0.6)",
              border: "2px dashed #0f0",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              pointerEvents: "none",
              fontFamily: "monospace",
              fontSize: 14,
              color: "#0f0",
            }}
          >
            Drop file to open (copy)
          </div>
        )}

        {/* GUTTER (inside its own moving layer) */}
        <div
          ref={gutterRef}
          style={{
            width: GUTTER_W,
            background: "#1b1b1b",
            color: "#888",
            textAlign: "right",
            paddingLeft: 4,
            paddingRight: 4,
            fontFamily: "monospace",
            overflow: "hidden",
            boxSizing: "border-box",
            position: "relative",
            zIndex: 3,
            userSelect: "none",
          }}
        >
          {/* ✅ Fake first line to match textarea + syntax */}
          {/* <div style={{ height: LINE_H, lineHeight: `${LINE_H}px` }}>blank</div> */}
          <div style={{ height: LINE_H }} />


          {lines.map((_, i) => (
            <div
              key={i}
              style={{
                height: LINE_H,
                lineHeight: `${LINE_H}px`,
                background: i === currentLine ? "#333" : "transparent",
              }}
            >
              {i + 1}
            </div>
          ))}
        </div>

        {/* EXECUTION HIGHLIGHT (also accounts fake top line) */}
        <div
          ref={execHighlightRef}
          style={{
            position: "absolute",
            left: GUTTER_W,
            right: 0,
            // top: (currentLine + 1) * LINE_H,
            top: (currentLine+1) * LINE_H,

            height: LINE_H,
            background: "rgba(255,255,255,0.08)",
            pointerEvents: "none",
            zIndex: 2,
          }}
        />

        {/* SYNTAX LAYER (must move with scrollLeft + scrollTop) */}
        <pre
          ref={syntaxRef}
          aria-hidden
          style={{
            position: "absolute",
            left: GUTTER_W,
            top: 0,
            margin: 0,
            padding: "0 10px",
            fontFamily: "monospace",
            fontSize: 13,
            lineHeight: `${LINE_H}px`,
            whiteSpace: "pre",
            overflow: "hidden",
            pointerEvents: "none",
            boxSizing: "border-box",
            userSelect: "none",
            tabSize: 2,
            zIndex: 1,
            paddingTop: LINE_H,
          }}
          dangerouslySetInnerHTML={{ __html: highlightedHTML }}
        />

        {/* TEXTAREA (real editor, invisible text, visible caret) */}
        <textarea
          ref={textareaRef}
          value={viewText}
          onChange={(e) => {
            // Strip the fake first line safely
            const raw = e.target.value || "";
            const next = raw.startsWith("\n") ? raw.slice(1) : raw;
            setCode(next);
            isAssembledRef.current = false;
            setActiveRunButton("edit");
          }}
          onScroll={onScroll}
          onClick={updateCursorLine}
          onKeyUp={updateCursorLine}
          onKeyDown={(e) => {
  if (
    e.ctrlKey &&
    !e.altKey &&
    (e.key === "/" || e.key === "?")
  ) {
    e.preventDefault();
    toggleComment();
  }
}}

          style={{
            flex: 1,
            background: "transparent",
            color: "transparent",
            caretColor: "#7FDBFF",
            border: "none",
            padding: "0 10px",
            fontFamily: "monospace",
            fontSize: 13,
            lineHeight: `${LINE_H}px`,
            paddingTop: LINE_H,
            resize: "none",
            outline: "none",
            overflow: "auto",
            whiteSpace: "pre",
            wordWrap: "normal",
            overflowX: "auto",
            boxSizing: "border-box",
            tabSize: 2,
            zIndex: 4, // on top to receive input
          }}
        />
      </div>
    </div>
  );
}
